/**
 * Lazy-loaded Cloudflare Turnstile widget. Pages that need a captcha import
 * `loadTurnstile()` on mount — the script tag is injected once globally and
 * subsequent calls reuse the same promise.
 *
 * When VITE_TURNSTILE_SITE_KEY is unset (local dev without a Cloudflare
 * account), `getSiteKey()` returns null and callers should skip rendering
 * the widget; the backend bypasses verification in the matching state
 * (TURNSTILE_SECRET_KEY also unset).
 */

const SCRIPT_URL = "https://challenges.cloudflare.com/turnstile/v0/api.js?onload=__tableTalkTurnstileOnload";
let loadPromise = null;

export function getSiteKey() {
  const key = import.meta.env.VITE_TURNSTILE_SITE_KEY;
  return typeof key === "string" && key.length > 0 ? key : null;
}

export function loadTurnstile() {
  if (typeof window === "undefined") {
    return Promise.reject(new Error("Turnstile loader called outside the browser."));
  }
  if (window.turnstile) {
    return Promise.resolve(window.turnstile);
  }
  if (loadPromise) return loadPromise;

  loadPromise = new Promise((resolve, reject) => {
    window.__tableTalkTurnstileOnload = () => {
      if (window.turnstile) resolve(window.turnstile);
      else reject(new Error("Turnstile loaded but window.turnstile is missing."));
    };
    const s = document.createElement("script");
    s.src = SCRIPT_URL;
    s.async = true;
    s.defer = true;
    s.onerror = () => reject(new Error("Failed to load Turnstile script."));
    document.head.appendChild(s);
  });
  return loadPromise;
}

/**
 * Mount a Turnstile widget into the given container element. Returns a
 * widgetId that the caller can pass to window.turnstile.reset(widgetId)
 * after a failed submit so the user can try again.
 */
export async function renderTurnstile(container, { siteKey, onToken, onExpired, theme = "light" }) {
  const turnstile = await loadTurnstile();
  return turnstile.render(container, {
    sitekey: siteKey,
    callback: (token) => onToken?.(token),
    "expired-callback": () => onExpired?.(),
    theme
  });
}
